import { useState, useCallback } from 'react';
import { useUpdateHoldPosition } from '@/hooks/useCharting';
import type { HoldPosition, ChartingStatus } from '@/types';

interface HoldPositionDropdownProps {
lineItemId: string;
currentValue: HoldPosition | null | undefined;
disabled?: boolean;
chartingStatus?: ChartingStatus | null;
onUpdate?: (newValue: HoldPosition) => void;
}

const HOLD_POSITION_OPTIONS: { value: HoldPosition; label: string }[] = [
{ value: 'primary', label: 'Primary' },
{ value: 'secondary', label: 'Secondary' },
{ value: 'tertiary', label: 'Tertiary' },
];

export function HoldPositionDropdown({
lineItemId,
currentValue,
disabled = false,
chartingStatus,
onUpdate,
}: HoldPositionDropdownProps) {
const updateHoldPosition = useUpdateHoldPosition();
const [error, setError] = useState<string | null>(null);

const isRequired =
chartingStatus === 'open' || chartingStatus === 'in_progress';

const handleChange = useCallback(
async (e: React.ChangeEvent<HTMLSelectElement>) => {
const newValue = e.target.value as HoldPosition;
if (!newValue) return; // ignore selecting the placeholder
setError(null);

try {
await updateHoldPosition.mutateAsync({
lineItemId,
holdPosition: newValue,
});
onUpdate?.(newValue);
} catch (err) {
// OPE-382: surface the server's message. `useUpdateHoldPosition` used to call
// `PUT /items/:id`, which validated nothing, so any failure here was genuinely
// exceptional and a generic string was the honest answer. It now calls
// `set-position`, which enforces the depth cap and the single-primary rule — so
// "unit already has 3 holds (maximum 3)" is an ordinary, actionable outcome, and
// replacing it with "Failed to update hold position" tells the chartist nothing they
// can act on. The generic text stays as the fallback for a failure carrying no message.
const msg = (err as { message?: string })?.message;
setError(msg || 'Failed to update hold position');
}
},
[lineItemId, updateHoldPosition, onUpdate],
);

return (
<div className="flex flex-col gap-1">
<label
htmlFor={`hold-position-${lineItemId}`}
className="text-xs font-semibold text-neutral-600 uppercase tracking-wide"
>
Hold Position{isRequired && <span className="text-red-500 ml-0.5">*</span>}
</label>
<select
id={`hold-position-${lineItemId}`}
value={currentValue ?? ''}
onChange={handleChange}
disabled={disabled || updateHoldPosition.isPending}
className={`
block w-full rounded-md border px-3 py-2 text-sm
focus:outline-none focus:ring-2 focus:ring-outfront-purple
${disabled ? 'bg-neutral-100 text-neutral-600 cursor-not-allowed' : 'bg-white text-neutral-900'}
${error ? 'border-red-300' : 'border-neutral-200'}
`}
>
{!currentValue && (
<option value="" disabled>
Not Set
</option>
)}
{HOLD_POSITION_OPTIONS.map((opt) => (
<option key={opt.value} value={opt.value}>
{opt.label}
</option>
))}
</select>
{updateHoldPosition.isPending && (
<span className="text-xs text-neutral-400">Saving…</span>
)}
{error && <span className="text-xs text-red-600">{error}</span>}
</div>
);
}
